import {
    cacheKey as FEATURE_FLAGS_CACHE_KEY,
    FEATURE_FLAG_NAMES,
} from '@tn/foundation/entities/feature-flags/constants';
import { getFeatureFlag } from '@tn/foundation/entities/feature-flags/utils/getFeatureFlag';
import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import request from 'graphql-request';
import { AnyEventObject, fromPromise, Mapper } from 'xstate';

import { FlowEntity } from '../../types';

import { cmpConsentTokenCacheKey, cmpConsentTokenDocument } from './graphql/constants';

export type FetchCmpConsentTokenInput = {
    userId: string;
};

export type FetchCmpConsentTokenOutput = {
    privacyId: string;
};

type ParentContextWithUserId = { userId: string };

/**
 * Fetches the backend-hashed privacy ID (read server-side from the `sub` claim of the
 * Paramount CMP consent token) from the signup GraphQL endpoint and forwards it to Optanon's
 * `cmp.ot.syncProfile(privacyId)` so the SDK can populate `localStorage.paramount_privacy_id`
 * for cross-device consent syncing. The raw `userId` must never be passed to `syncProfile`.
 *
 * Gated by `tn_ketch_enabled` feature flag + `options.consent.syncProfileEnabled`
 * blueprint key (US_GENERAL jurisdiction only per Paramount Privacy spec).
 * Failures resolve quietly so the sign-in flow can complete regardless.
 */
export const fetchCmpConsentTokenActor: FlowEntity = {
    initActor: ({ queryClient, getConfigByKey }) =>
        fromPromise<FetchCmpConsentTokenOutput, FetchCmpConsentTokenInput>(async ({ input }) => {
            const { userId } = input;

            const featureFlagsData = queryClient.getQueryData<{
                featureFlags?: Record<string, unknown>;
            }>([FEATURE_FLAGS_CACHE_KEY]);
            const featureFlags = featureFlagsData?.featureFlags || {};
            const isKetchEnabled = getFeatureFlag(featureFlags)(
                FEATURE_FLAG_NAMES.KETCH_ENABLED,
                false,
            );
            const isSyncProfileEnabled = Boolean(
                getConfigByKey('options.consent.syncProfileEnabled'),
            );

            if (!userId || !isKetchEnabled || !isSyncProfileEnabled) {
                return { privacyId: '' };
            }

            try {
                const data = await request<{
                    cmpConsentToken: { success: boolean; token: string | null };
                }>(
                    '/api/tn/signup/graphql/',
                    cmpConsentTokenDocument,
                    {},
                    { 'request-source': cmpConsentTokenCacheKey },
                );
                const privacyId = data?.cmpConsentToken?.token ?? '';

                if (privacyId && globalThis.window?.cbsoptanon?.cmd) {
                    globalThis.window.cbsoptanon.cmd.push((cmp) => {
                        cmp?.ot?.syncProfile?.(privacyId);
                    });
                }

                return { privacyId };
            } catch (err) {
                // Swallow — privacy SDK side-effect must not block sign-in UX.
                // Resolver-level errors are logged server-side.
                // Log client-side too so network/parse failures are visible in production.
                appLogger.error(
                    'fetchCmpConsentToken failed',
                    { component: 'fetchCmpConsentTokenActor' },
                    err,
                );
                return { privacyId: '' };
            }
        }),

    inputActor: <
        Mapper<ParentContextWithUserId, AnyEventObject, FetchCmpConsentTokenInput, AnyEventObject>
    >(({ context }) => {
        return { userId: context.userId };
    }),
};
